import { attachAnchoredCalloutController } from "./anchored-callout-controller.js";

const PANEL_ID = "fishy-guide-link-preview";
const GUIDE_PATH_PATTERN = /^\/(?:[a-z]{2}-[A-Z]{2}\/)?guides\/[^/]+/u;
const PROJECTION_FIELDS = Object.freeze([
    "cache_age",
    "fishystuff_authors",
    "fishystuff_description",
    "fishystuff_reading_minutes",
    "height",
    "html",
    "thumbnail_url",
    "title",
    "type",
    "version",
    "width",
]);
const attachedRoots = new WeakMap();

function trimString(value) {
    return String(value ?? "").trim();
}

function requiredString(value, field) {
    const normalized = trimString(value);
    if (!normalized) {
        throw new TypeError(`Guide preview ${field} must be a non-empty string`);
    }
    return normalized;
}

function positiveInteger(value, field) {
    if (!Number.isSafeInteger(value) || value < 1) {
        throw new TypeError(`Guide preview ${field} must be a positive integer`);
    }
    return value;
}

function exactFields(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError("Guide preview projection must be an object");
    }
    const actual = Object.keys(value).sort();
    const expected = [...PROJECTION_FIELDS].sort();
    if (actual.length !== expected.length || actual.some((field, index) => field !== expected[index])) {
        throw new TypeError("Guide preview projection fields must match the v1 contract exactly");
    }
}

export function parseGuidePreviewProjection(value) {
    exactFields(value);
    if (value.type !== "rich" || value.version !== "1.0") {
        throw new TypeError("Guide preview projection must be rich oEmbed version 1.0");
    }
    if (!Array.isArray(value.fishystuff_authors) || value.fishystuff_authors.length < 1) {
        throw new TypeError("Guide preview authors must be a non-empty array");
    }
    const thumbnailUrl = new URL(requiredString(value.thumbnail_url, "thumbnail_url"));
    if (!/^https?:$/u.test(thumbnailUrl.protocol)) {
        throw new TypeError("Guide preview thumbnail_url must use HTTP(S)");
    }
    return Object.freeze({
        authors: Object.freeze(value.fishystuff_authors.map((author) => (
            requiredString(author, "author")
        ))),
        description: requiredString(value.fishystuff_description, "description"),
        readingMinutes: positiveInteger(
            value.fishystuff_reading_minutes,
            "reading minutes",
        ),
        thumbnailUrl: thumbnailUrl.href,
        title: requiredString(value.title, "title"),
    });
}

function routePath(pathname) {
    const normalized = pathname.endsWith("/") ? pathname : `${pathname}/`;
    return normalized.replace(/\/{2,}/gu, "/");
}

export function guidePreviewRequest(anchorHref, locationHref) {
    let target;
    let current;
    try {
        current = new URL(locationHref);
        target = new URL(anchorHref, current);
    } catch {
        return null;
    }
    if (
        !/^https?:$/u.test(target.protocol)
        || target.origin !== current.origin
        || !GUIDE_PATH_PATTERN.test(target.pathname)
    ) {
        return null;
    }
    target.pathname = routePath(target.pathname);
    target.search = "";
    target.hash = "";
    if (target.pathname === routePath(current.pathname)) {
        return null;
    }
    const artifactPath = target.pathname.replace(
        /^\/[a-z]{2}-[A-Z]{2}(?=\/guides\/)/u,
        "",
    );
    const oembed = new URL(`/oembed${artifactPath}index.json`, current);
    oembed.searchParams.set("url", target.href);
    oembed.searchParams.set("format", "json");
    return Object.freeze({
        canonicalUrl: target.href,
        oembedUrl: oembed.href,
    });
}

function clamp(value, minimum, maximum) {
    if (maximum < minimum) {
        return minimum;
    }
    return Math.min(maximum, Math.max(minimum, value));
}

export function guidePreviewViewportPosition({
    anchorRect,
    panelRect,
    viewportHeight,
    viewportWidth,
}) {
    const margin = 12;
    const gap = 10;
    const width = Number(panelRect?.width ?? 0);
    const height = Number(panelRect?.height ?? 0);
    const left = clamp(
        Number(anchorRect?.left ?? 0) + Number(anchorRect?.width ?? 0) / 2 - width / 2,
        margin,
        Number(viewportWidth ?? 0) - margin - width,
    );
    const above = Number(anchorRect?.top ?? 0) - gap - height;
    const below = Number(anchorRect?.bottom ?? 0) + gap;
    const top = above >= margin
        ? above
        : clamp(below, margin, Number(viewportHeight ?? 0) - margin - height);
    return Object.freeze({ left, top });
}

function previewAnchor(root, target, locationHref) {
    const anchor = target?.closest?.("a[href]") ?? null;
    if (!anchor || !root.contains?.(anchor)) {
        return null;
    }
    return guidePreviewRequest(anchor.href ?? anchor.getAttribute?.("href"), locationHref)
        ? anchor
        : null;
}

function ensurePanel(documentRef) {
    const existing = documentRef.getElementById?.(PANEL_ID);
    if (existing) {
        return existing;
    }
    const panel = documentRef.createElement("div");
    panel.id = PANEL_ID;
    panel.hidden = true;
    panel.className = "card card-side card-sm card-border not-prose fixed z-[93] w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden bg-base-100 shadow-2xl";
    panel.setAttribute("data-guide-link-preview", "");
    documentRef.body?.appendChild(panel);
    return panel;
}

function appendText(documentRef, parent, tagName, className, text) {
    const element = documentRef.createElement(tagName);
    element.className = className;
    element.textContent = text;
    parent.appendChild(element);
    return element;
}

function renderLoading(documentRef, panel, label) {
    panel.replaceChildren();
    const body = documentRef.createElement("div");
    body.className = "card-body flex-row items-center gap-3 p-4 text-sm text-base-content/70";
    const spinner = documentRef.createElement("span");
    spinner.className = "loading loading-spinner loading-sm";
    spinner.setAttribute("aria-hidden", "true");
    body.appendChild(spinner);
    appendText(documentRef, body, "span", "", label);
    panel.appendChild(body);
}

function renderPreview(documentRef, panel, preview, labels) {
    panel.replaceChildren();
    const figure = documentRef.createElement("figure");
    figure.className = "m-3 me-0 grid aspect-[3/2] w-24 shrink-0 place-items-center self-center overflow-hidden rounded-box bg-base-200";
    const image = documentRef.createElement("img");
    image.src = preview.thumbnailUrl;
    image.alt = "";
    image.loading = "lazy";
    image.decoding = "async";
    image.className = "block h-full w-full object-cover";
    figure.appendChild(image);

    const body = documentRef.createElement("div");
    body.className = "card-body min-w-0 gap-2 p-4";
    appendText(documentRef, body, "div", "card-title text-sm leading-snug", preview.title);
    appendText(
        documentRef,
        body,
        "p",
        "m-0 line-clamp-3 text-xs leading-relaxed text-base-content/70",
        preview.description,
    );
    appendText(
        documentRef,
        body,
        "div",
        "mt-auto text-xs text-base-content/55",
        `${labels.by} ${preview.authors.join(", ")} • ~${preview.readingMinutes} ${labels.minutes}`,
    );
    panel.append(figure, body);
}

export function attachGuideLinkPreview(root, {
    documentRef = globalThis.document,
    fetchRef = globalThis.fetch,
    locationHref = globalThis.location?.href,
    windowRef = globalThis.window,
} = {}) {
    if (
        !root
        || typeof root.addEventListener !== "function"
        || !documentRef?.body
        || typeof documentRef.createElement !== "function"
        || typeof fetchRef !== "function"
        || !locationHref
    ) {
        return () => {};
    }
    const existing = attachedRoots.get(root);
    if (existing) {
        return existing;
    }
    const panel = ensurePanel(documentRef);
    const labelHost = root.closest?.("[data-link-preview-labels]") ?? null;
    const labels = Object.freeze({
        by: trimString(labelHost?.dataset?.linkPreviewBy) || "By",
        loading: trimString(labelHost?.dataset?.linkPreviewLoading) || "Loading…",
        minutes: trimString(labelHost?.dataset?.linkPreviewMinutes) || "min read",
    });
    const states = new Map();
    let activeAnchor = null;
    let controller = null;

    const requestForAnchor = (anchor) => guidePreviewRequest(
        anchor?.href ?? anchor?.getAttribute?.("href"),
        locationHref,
    );

    const load = (request) => {
        const existingState = states.get(request.oembedUrl);
        if (existingState) {
            return existingState;
        }
        const abortController = new AbortController();
        const state = { abortController, preview: null, status: "loading" };
        states.set(request.oembedUrl, state);
        Promise.resolve(fetchRef(request.oembedUrl, {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
            signal: abortController.signal,
        })).then((response) => {
            if (!response?.ok) {
                throw new Error(`Guide preview request failed with HTTP ${response?.status ?? "unknown"}`);
            }
            return response.json();
        }).then((value) => {
            state.preview = parseGuidePreviewProjection(value);
            state.status = "ready";
            if (requestForAnchor(activeAnchor)?.oembedUrl === request.oembedUrl) {
                controller?.refreshAnchor(activeAnchor);
            }
        }).catch((error) => {
            if (abortController.signal.aborted || error?.name === "AbortError") {
                return;
            }
            state.status = "unavailable";
            if (requestForAnchor(activeAnchor)?.oembedUrl === request.oembedUrl) {
                controller?.dismissAnchor(activeAnchor);
            }
        });
        return state;
    };

    const position = () => {
        if (!activeAnchor || panel.hidden) {
            return;
        }
        const next = guidePreviewViewportPosition({
            anchorRect: activeAnchor.getBoundingClientRect?.(),
            panelRect: panel.getBoundingClientRect?.(),
            viewportHeight: Number(windowRef?.innerHeight ?? 0),
            viewportWidth: Number(windowRef?.innerWidth ?? 0),
        });
        panel.style.left = `${next.left}px`;
        panel.style.top = `${next.top}px`;
    };

    controller = attachAnchoredCalloutController(documentRef, {
        id: PANEL_ID,
        pinnable: false,
        panel: () => panel,
        resolveAnchor(target) {
            return previewAnchor(root, target, locationHref);
        },
        present(anchor) {
            const request = requestForAnchor(anchor);
            if (!request) {
                return null;
            }
            activeAnchor = anchor;
            const state = load(request);
            if (state.status === "unavailable") {
                return null;
            }
            if (state.status === "ready") {
                renderPreview(documentRef, panel, state.preview, labels);
            } else {
                renderLoading(documentRef, panel, labels.loading);
            }
            return { panel };
        },
        position,
        onDismiss() {
            activeAnchor = null;
        },
    });

    const onViewportChange = () => position();
    windowRef?.addEventListener?.("resize", onViewportChange);
    windowRef?.addEventListener?.("scroll", onViewportChange, true);

    const dispose = () => {
        controller?.dispose();
        for (const state of states.values()) {
            state.abortController.abort();
        }
        states.clear();
        panel.remove?.();
        windowRef?.removeEventListener?.("resize", onViewportChange);
        windowRef?.removeEventListener?.("scroll", onViewportChange, true);
        attachedRoots.delete(root);
    };
    attachedRoots.set(root, dispose);
    return dispose;
}

export function attachGuideLinkPreviews(documentRef = globalThis.document) {
    const roots = Array.from(
        documentRef?.querySelectorAll?.("[data-content-link-root]") ?? [],
    );
    const disposers = roots.map((root) => attachGuideLinkPreview(root, { documentRef }));
    return () => disposers.forEach((dispose) => dispose());
}
